"use client";

import { useSearchParams } from "next/navigation";
import React, { useState } from "react";

import { useTranslations } from "next-intl";

import { EnumViewMode } from "@/enums/view-mode";
import { useCurrencyStore } from "@/store/currency.store";
import { EmptyState } from "@/ui/Common/EmptyState";
import { ObjectList } from "@/ui/Common/ObjectList";
import {
  filterLayouts,
  parseLayoutsFilters,
  sortLayouts,
} from "@/utils/filterLayouts";
import { FilterPanel } from "@/widgets/Filter/Panel/FilterPanel";
import { PlanCard } from "@/widgets/PlanCard";

interface Layout {
  _id: string;
  coverImage?: string;
  rooms?: number;
  area?: number;
  pricePerSqM?: number;

  [key: string]: any;
}

interface DeveloperPlansListProps {
  propertyData?: any;
  layouts: Layout[];
  locale: string;
  slug: string;
  name: string | null;
  totalCount?: number;
}

export default function DeveloperPlansList({
  propertyData,
  layouts,
  locale,
  slug,
  name,
}: DeveloperPlansListProps) {
  const t = useTranslations();
  const [viewMode] = useState(EnumViewMode.GRID);
  const searchParams = useSearchParams();
  const { currency } = useCurrencyStore();

  // Parse current filters from URL
  const currentFilters = React.useMemo(() => {
    if (!searchParams) return {};
    return parseLayoutsFilters(new URLSearchParams(searchParams.toString()));
  }, [searchParams]);

  // Apply filtering and sorting to layouts based on current currency
  const filteredLayouts = React.useMemo(() => {
    return filterLayouts(layouts, currentFilters, currency.value);
  }, [layouts, currentFilters, currency.value]);

  const sortedLayouts = React.useMemo(() => {
    return sortLayouts(filteredLayouts, currentFilters.sortBy, currency.value);
  }, [filteredLayouts, currentFilters.sortBy, currency.value]);

  const isEmpty = !sortedLayouts?.length;

  const completedDate = {
    quartal: propertyData?.projectInfo?.completionQuarter || 1,
    year: propertyData?.projectInfo?.completionYear || new Date().getFullYear(),
  };

  return (
    <div>
      <h1 className="text-[2rem] md:text-[3rem] font-medium mb-8">
        {t("Sale of apartments in")} {name || t("Property")}
      </h1>

      <FilterPanel
        currentFilters={currentFilters}
        itemsCount={sortedLayouts.length}
        className="mb-8"
      />

      <ObjectList isEmpty={isEmpty}>
        <ObjectList.Items viewMode={viewMode}>
          {sortedLayouts.map((layout: Layout) => (
            <PlanCard
              key={layout._id}
              layout={layout}
              locale={locale}
              slug={slug}
              location={propertyData.location}
              completedDate={completedDate}
            />
          ))}
        </ObjectList.Items>
        <ObjectList.Empty>
          <EmptyState
            message={t("No floor plans available for this development")}
          />
        </ObjectList.Empty>
      </ObjectList>
    </div>
  );
}
